import { useState } from "react";
import { API } from '../../config/api';
import axios from "axios";
import { errorMessage } from "../../utils/errorMessage";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { SubmissionLayout, StatusBadge } from "../../components/SubmissionLayout";
import BonsaiMate from "../../components/BonsaiMate";
import { Shield, Search, FileText, User, Download, Loader2, FileJson, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

function FinancialRow({ label, value, accent = false }) {
  return (
    <div className="flex justify-between items-center py-2 border-b border-white/10/40 last:border-0">
      <span className="text-sm text-txt-faint">{label}</span>
      <span className={`text-sm font-medium ${accent ? 'text-teal' : 'text-txt-faint'}`}>
        {value || "\u2014"}
      </span>
    </div>
  );
}

function RiskBadge({ level }) {
  const styles = {
    high: "bg-status-red-dim text-status-red border-red-200",
    low: "bg-teal-dim text-teal border-teal",
    medium: "bg-status-amber-dim text-status-amber border-amber-200",
  };
  return (
    <span className={`px-3 py-1 rounded-md text-xs font-semibold border ${styles[level] || styles.medium}`}>
      {(level || "medium").toUpperCase()}
    </span>
  );
}

export default function RiskAssessmentDetail({ submission, formatDate, handleExportJson, navigate, isDirector, onDelete }) {
  const data = submission?.data || {};
  const [raMatterRef, setRaMatterRef] = useState(data.matter_reference || '');
  const [raFiling, setRaFiling] = useState(false);
  const [raFiled, setRaFiled] = useState(!!data.filed_to_sharepoint);

  // Read order:
  //  1. ``override_band`` / ``override_risk_band`` — supervisor override (post-30-Apr-2026 canonical / legacy alias).
  //  2. ``calculated_risk_band`` — server-computed band from the 8-answer System A flow.
  //  3. legacy fields (``overall_risk_calculated`` / ``overall_risk``) — kept so old records still render.
  const computedBand = (data.calculated_risk_band || data.overall_risk_calculated || "").toLowerCase();
  const overrideBand = (data.override_band || data.override_risk_band || "").toLowerCase();
  const overallRisk = overrideBand || computedBand || (data.overall_risk || "medium").toLowerCase();

  const actions = (
    <>
      <Button onClick={handleExportJson} variant="outline" size="sm" className="border-navy-border text-teal hover:bg-teal/5 gap-1 text-xs h-8" data-testid="ra-export-json">
        <FileJson className="w-3.5 h-3.5" /> JSON
      </Button>
      <Button variant="outline" size="sm" className="border-navy-border text-teal hover:bg-teal/5 gap-1 text-xs h-8"
        data-testid="ra-download-pdf"
        onClick={async () => {
          try {
            const res = await axios.get(`${API}/submissions/${submission.id}/download-risk-assessment-pdf`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const a = document.createElement('a');
            a.href = url;
            a.download = `Risk_Assessment_${(data.client_name || 'Client').replace(/\s/g, '_')}.pdf`;
            a.click();
            window.URL.revokeObjectURL(url);
          } catch {
            toast.error('Failed to download PDF');
          }
        }}
      >
        <Download className="w-3.5 h-3.5" /> PDF
      </Button>
    </>
  );

  return (
    <>
    <SubmissionLayout
      navigate={navigate}
      title={data.matter_title || "Risk Assessment"}
      subtitle={`${data.client_name || ""} \u2014 ${data.assessment_type === "midpoint" ? "Mid-Point Review" : "Standalone"}`}
      matterReference={data.matter_reference}
      badge="Risk Assessment"
      badgeColor="amber"
      badgeIcon={Shield}
      isTest={data.is_test_submission}
      isDirector={isDirector}
      onDelete={onDelete}
      actions={actions}
    >
      {/* Status Row */}
      <div className="flex items-center justify-between bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 px-5 py-3.5 shadow-sm" data-testid="ra-header-info">
        <div className="flex items-center gap-4">
          <StatusBadge status={submission.status} />
          <RiskBadge level={overallRisk} />
          <span className="text-sm text-txt-faint">Submitted {formatDate(submission.created_at)}</span>
        </div>
        <span className="text-xs text-txt-muted font-mono tracking-wide">{submission.id?.slice(0, 8)}</span>
      </div>

      {/* Bento Grid */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4" data-testid="ra-bento-grid">

        {/* Matter Information — 8 cols */}
        <div className="md:col-span-8 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-matter-info">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <User className="w-4 h-4" /> Matter Information
            </h2>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            <div className="flex items-start gap-3 py-3 border-b border-white/10/60">
              <div className="w-8 h-8 rounded-md bg-teal/8 flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-4 h-4 text-teal" />
              </div>
              <div>
                <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block">Client</span>
                <span className="text-base font-medium text-txt-faint">{data.client_name || "\u2014"}</span>
              </div>
            </div>
            <div className="flex items-start gap-3 py-3 border-b border-white/10/60">
              <div className="w-8 h-8 rounded-md bg-teal/8 flex items-center justify-center shrink-0 mt-0.5">
                <FileText className="w-4 h-4 text-teal" />
              </div>
              <div>
                <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block">Matter Title</span>
                <span className="text-sm font-medium text-txt-faint">{data.matter_title || "\u2014"}</span>
              </div>
            </div>
            <div className="flex items-start gap-3 py-3 border-b border-white/10/60">
              <div className="w-8 h-8 rounded-md bg-teal/8 flex items-center justify-center shrink-0 mt-0.5">
                <FileText className="w-4 h-4 text-teal" />
              </div>
              <div>
                <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block">Reference</span>
                <span className="text-base font-medium text-txt-faint">{data.matter_reference || "\u2014"}</span>
              </div>
            </div>
            <div className="flex items-start gap-3 py-3 border-b border-white/10/60">
              <div className="w-8 h-8 rounded-md bg-teal/8 flex items-center justify-center shrink-0 mt-0.5">
                <User className="w-4 h-4 text-teal" />
              </div>
              <div>
                <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block">Fee Earner</span>
                <span className="text-sm font-medium text-txt-faint">{data.fee_earner || "\u2014"}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Overall Risk — 4 cols */}
        <div className="md:col-span-4 space-y-4">
          <div className="bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-overall-risk">
            <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <Shield className="w-4 h-4" /> Overall Risk
              </h2>
            </div>
            <div className="p-5 text-center space-y-3">
              <div className={`inline-flex items-center px-6 py-3 rounded-md text-lg font-bold border-2 ${
                overallRisk === "high" ? "bg-status-red-dim text-status-red border-red-300"
                : overallRisk === "low" ? "bg-teal-dim text-teal border-teal"
                : "bg-status-amber-dim text-status-amber border-amber-300"
              }`}>
                {overallRisk.toUpperCase()}
              </div>
              {overrideBand && computedBand && overrideBand !== computedBand && (
                <p className="text-xs text-status-amber bg-status-amber-dim px-3 py-1.5 rounded-md border border-amber-200">
                  Supervisor override: {overrideBand.toUpperCase()} (computed was {computedBand.toUpperCase()})
                </p>
              )}
            </div>
          </div>

          {/* Key Dates */}
          <div className="bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-key-dates">
            <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
              <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
                <Clock className="w-4 h-4" /> Key Dates
              </h2>
            </div>
            <div className="p-5 space-y-1">
              <FinancialRow label="Key Deadline" value={data.key_deadline} accent />
              <FinancialRow label="Next Review" value={data.review_date} accent />
              <FinancialRow label="Assessed" value={data.assessed_at ? formatDate(data.assessed_at) : "\u2014"} />
            </div>
          </div>
        </div>

        {/* Risk Factor Ratings — 12 cols */}
        <div className="md:col-span-12 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-risk-factors">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <AlertTriangle className="w-4 h-4" /> Risk Factor Ratings
            </h2>
          </div>
          <div className="p-5">
            {data.risk_factors && Object.keys(data.risk_factors).length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1">
                {Object.entries(data.risk_factors).map(([factor, level]) => (
                  <div key={factor} className="flex items-center justify-between py-2 border-b border-white/10/40">
                    <span className="text-sm text-txt-faint capitalize">{factor.replace(/_/g, " ")}</span>
                    <RiskBadge level={level} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-txt-muted">No individual risk factors rated</p>
            )}
          </div>
        </div>

        {/* Narrative & Mitigations — 12 cols */}
        <div className="md:col-span-12 bg-white/80 backdrop-blur-sm rounded-md border border-teal/10 shadow-sm overflow-hidden" data-testid="section-narrative">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <FileText className="w-4 h-4" /> Risk Narrative & Mitigations
            </h2>
          </div>
          <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { label: "Material Risks", value: data.material_risks },
              { label: "Risk Narrative", value: data.risk_narrative },
              { label: "Mitigating Actions", value: data.mitigating_actions },
              { label: "Supervisor Notes", value: data.supervisor_notes },
            ].map(({ label, value }) => (
              <div key={label}>
                <span className="text-xs font-medium text-txt-faint uppercase tracking-wider block mb-1">{label}</span>
                <p className="text-sm text-txt-faint leading-relaxed">{value || "\u2014"}</p>
              </div>
            ))}
          </div>
        </div>

        {/* File to SharePoint — 12 cols */}
        <div className={`md:col-span-12 rounded-md border-2 shadow-sm overflow-hidden ${raFiled ? 'border-teal bg-teal-dim/80' : 'border-teal/20 bg-white/80'}`} data-testid="ra-file-to-sp">
          <div className="px-5 py-4 border-b border-white/10/60 bg-[color:var(--surface-inset)]">
            <h2 className="font-semibold text-teal flex items-center gap-2 text-sm font-sans">
              <Shield className="w-4 h-4" /> {raFiled ? 'Filed to SharePoint' : 'File to SharePoint'}
            </h2>
          </div>
          <div className="p-5">
            {raFiled ? (
              <div className="flex items-center gap-2 text-sm text-teal">
                <CheckCircle2 className="w-4 h-4" />
                <span>Risk assessment PDF filed to {raMatterRef}/Compliance and Financials.</span>
              </div>
            ) : (
              <div className="flex flex-col sm:flex-row gap-3">
                <p className="text-xs text-txt-muted sm:hidden">Enter the full matter reference to file this risk assessment PDF to the compliance folder.</p>
                <input
                  type="text"
                  value={raMatterRef}
                  onChange={(e) => setRaMatterRef(e.target.value)}
                  placeholder="e.g. BL.VEC.006.0096"
                  className="flex-1 px-3 py-2 text-sm border border-white/10 rounded-md focus:outline-none focus:ring-2 focus:ring-teal/30 bg-white"
                  data-testid="ra-matter-ref-input"
                />
                <Button
                  onClick={async () => {
                    if (!raMatterRef.trim()) return;
                    setRaFiling(true);
                    try {
                      const res = await axios.post(`${API}/submissions/${submission.id}/file-risk-assessment`, { matter_reference: raMatterRef.trim() });
                      if (res.data.success) { setRaFiled(true); toast.success(res.data.message); }
                      else toast.error('Filing failed');
                    } catch (err) { toast.error(errorMessage(err, 'Filing failed')); }
                    finally { setRaFiling(false); }
                  }}
                  disabled={raFiling || !raMatterRef.trim()}
                  className="bg-teal hover:bg-teal-hover text-white text-sm px-6"
                  data-testid="ra-file-btn"
                >
                  {raFiling ? <Loader2 className="w-4 h-4 animate-spin" /> : 'File PDF'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </SubmissionLayout>
    {data.matter_reference && (
      <BonsaiMate context="matter" matterReference={data.matter_reference} />
    )}
    </>
  );
}
